import {
  Badge,
  Box,
  Divider,
  Flex,
  IconButton,
  Text,
  VStack,
  Image,
  keyframes,
} from '@chakra-ui/react';
import { ConcertResponse, MatchState } from 'api/responseTypes';
import { useEventTracking } from 'hooks/useEventTracking';
import { FormattedMessage } from 'react-intl';
import { MouseEvent, useState } from 'react';
import { Share2 } from 'lucide-react';
import { EventShareKind, shareEvent } from 'utils/shareEvent';

const pulsate = keyframes`
  0% { 
    opacity: 1; 
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(255, 235, 59, 0.7);
  }
  50% { 
    opacity: 0.9; 
    transform: scale(1.02);
    box-shadow: 0 0 0 8px rgba(255, 235, 59, 0);
  }
  100% { 
    opacity: 1; 
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(255, 235, 59, 0);
  }
`;

const pulsateAnimation = `${pulsate} 2s ease-in-out infinite`;

interface ConcertCardProps extends ConcertResponse {
  onCardClick: () => void;
  typeOfEvent?: 'concert' | 'workshop' | 'international';
}

export const ConcertCard = ({
  state,
  onCardClick,
  date,
  time,
  imageLink,
  startingPrice,
  currency,
  title,
  city,
  eventKey,
  typeOfEvent = 'concert',
  isFreeEvent,
  hasTimeslots,
}: ConcertCardProps) => {
  const [isSharing, setIsSharing] = useState(false);
  const { eventTracking } = useEventTracking();

  const handleShareClick = async (e: MouseEvent) => {
    e.stopPropagation();
    if (isSharing) return;

    const eventName = title || eventKey;

    eventTracking.share.buttonClicked({
      typeOfEvent,
      eventName,
    });

    // International events share the detail route; concerts/workshops keep the concert route.
    const shareKind: EventShareKind = typeOfEvent === 'international' ? 'international' : true;

    setIsSharing(true);
    try {
      await shareEvent(eventName, eventKey, shareKind);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '';

      if (errorMessage.includes('AbortError') || errorMessage.includes('cancel')) {
        return;
      }
    } finally {
      setIsSharing(false);
    }
  };

  return (
    <Box
      as='li'
      listStyleType='none'
      bg='white'
      borderRadius='12px'
      borderWidth='1px'
      borderColor={state === MatchState.SOLD_OUT ? 'dark.D100' : 'dark.D100'}
      overflow='hidden'
      cursor={
        state === MatchState.SOLD_OUT || state === MatchState.PREVIEW ? 'not-allowed' : 'pointer'
      }
      pointerEvents={
        state === MatchState.SOLD_OUT || state === MatchState.PREVIEW ? 'none' : 'unset'
      }
      onClick={onCardClick}
      transition='all 0.2s ease'
      _hover={{
        borderColor:
          state === MatchState.SOLD_OUT || state === MatchState.PREVIEW
            ? 'dark.D100'
            : 'green.G500',
        shadow: state === MatchState.SOLD_OUT || state === MatchState.PREVIEW ? 'none' : 'md',
        transform:
          state === MatchState.SOLD_OUT || state === MatchState.PREVIEW
            ? 'none'
            : 'translateY(-2px)',
      }}
      boxShadow={state === MatchState.SOLD_OUT || state === MatchState.PREVIEW ? 'none' : 'sm'}
      width='100%'
      height='100%'
      display='flex'
      flexDirection='column'
      position='relative'
      tabIndex={0}
      aria-label={`${title} event${date ? ` on ${date}` : ''}${city ? ` at ${city}` : ''}`}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (
          (e.key === 'Enter' || e.key === ' ') &&
          state !== MatchState.SOLD_OUT &&
          state !== MatchState.PREVIEW
        ) {
          e.preventDefault();
          onCardClick();
        }
      }}
      dir='rtl'
    >
      {state !== MatchState.PREVIEW && (
        <IconButton
          aria-label='Share event'
          icon={<Share2 size={16} />}
          size='sm'
          position='absolute'
          top={3}
          left={3}
          zIndex={3}
          bg='#E0F2F1'
          color='#4DB6AC'
          borderRadius='lg'
          _hover={{ bg: '#B2DFDB', color: '#00897B' }}
          _active={{ bg: '#80CBC4' }}
          isDisabled={isSharing}
          onClick={handleShareClick}
        />
      )}
      {/* Image Container */}
      <Box
        position='relative'
        width='100%'
        overflow='hidden'
        bg='dark.D100'
        aspectRatio='1/1'
        display='flex'
        alignItems='center'
        justifyContent='center'
      >
        <Image
          src={imageLink}
          alt={title}
          width='100%'
          height='100%'
          objectFit='cover'
          objectPosition='center'
          filter={state === MatchState.SOLD_OUT ? 'grayscale(100%) opacity(0.7)' : 'none'}
        />
        {/* Gradient Overlay */}
        <Box
          position='absolute'
          bottom='0'
          left='0'
          right='0'
          height='80px'
          bgGradient='linear(to-t, rgba(0,0,0,0.4), transparent)'
          pointerEvents='none'
        />
        {/* Price Badge */}
        {state !== MatchState.PREVIEW && (
          <Box position='absolute' bottom={3} left={3} zIndex={2}>
            <Badge
              bg={isFreeEvent ? 'teal.500' : 'white'}
              color={isFreeEvent ? 'white' : 'green.G500'}
              px={3}
              py={1.5}
              borderRadius='full'
              fontSize='sm'
              fontWeight='600'
              boxShadow='sm'
              textStyle='bodySmallSemibold'
            >
              {isFreeEvent ? (
                <FormattedMessage defaultMessage='Free' id='freeEvent.label' />
              ) : (
                <>
                  <FormattedMessage defaultMessage='From' id='concerts.upcoming.from' />
                  {` ${new Intl.NumberFormat().format(startingPrice)} ${currency}`}
                </>
              )}
            </Badge>
          </Box>
        )}
        {/* Preview Badge */}
        {state === MatchState.PREVIEW && (
          <Box position='absolute' bottom={3} left={3} zIndex={2}>
            <Badge
              bg='yellow.Y500'
              color='dark.D900'
              px={3}
              py={1.5}
              borderRadius='full'
              fontSize='sm'
              fontWeight='600'
              textStyle='bodySmallSemibold'
              animation={pulsateAnimation}
            >
              <FormattedMessage defaultMessage='Preview' id='concerts.preview' />
            </Badge>
          </Box>
        )}
        {/* Sold Out Badge */}
        {state === MatchState.SOLD_OUT && (
          <Box position='absolute' top={3} right={3} zIndex={2}>
            <Badge
              bg='red'
              color='white'
              px={3}
              py={1.5}
              borderRadius='full'
              fontSize='sm'
              fontWeight='600'
              textStyle='bodySmallSemibold'
            >
              <FormattedMessage defaultMessage='Sold Out' id='concerts.soldOut' />
            </Badge>
          </Box>
        )}
      </Box>

      {/* Content */}
      <VStack spacing={4} align='start' mt={2} p={4}>
        <Text fontWeight='bold' fontSize='lg'>
          {title}
        </Text>
        <Divider />
        {hasTimeslots ? (
          <Text
            textStyle='caption'
            color={state === MatchState.SOLD_OUT ? 'dark.D200' : 'dark.D500'}
            fontWeight='bold'
          >
            حدث متعدد الاوقات
          </Text>
        ) : (
          date && (
            <Text
              textStyle='caption'
              color={state === MatchState.SOLD_OUT ? 'dark.D200' : 'dark.D500'}
              fontWeight='bold'
            >
              {date} {time}
            </Text>
          )
        )}
        <Flex w='100%' justify='space-between' align='center' mt={1}>
          {city && (
            <Text
              fontWeight='bold'
              fontSize='sm'
              textStyle='bodySmallRegular'
              color={state === MatchState.SOLD_OUT ? 'dark.D500' : '#008674'}
            >
              {city}
            </Text>
          )}
        </Flex>
      </VStack>
    </Box>
  );
};
